import { useEffect, useState } from "react";
import { scrollToId } from "@/lib/smoothScroll";
import { Menu, X } from "lucide-react";

const NAV = [
  { label: "Product", id: "product" },
  { label: "Pricing", id: "pricing" },
  { label: "About", id: "about" },
  { label: "Contact", id: "contact" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "border-b border-hairline bg-bg-primary/85 backdrop-blur-xl"
          : "border-b border-transparent bg-transparent"
      }`}
    >
      <div className="container-max band-pad flex h-[70px] items-center justify-between">
        <button
          data-testid="logo-home"
          onClick={() => scrollToId("top")}
          className="flex items-center gap-2.5"
          aria-label="RelayOps home"
        >
          <span className="grid h-8 w-8 place-items-center overflow-hidden rounded-lg ring-1 ring-signal/30">
            <img src="/logo.webp" alt="RelayOps" className="h-full w-full object-cover" />
          </span>
          <span className="font-display text-lg font-semibold tracking-tight text-ink">
            Relay<span className="text-signal">Ops</span>
          </span>
        </button>

        <nav className="hidden items-center gap-9 md:flex">
          {NAV.map((n) => (
            <button
              key={n.id}
              data-testid={`nav-${n.id}`}
              onClick={() => go(n.id)}
              className="group relative font-sans text-sm text-ink-muted transition-colors hover:text-ink"
            >
              {n.label}
              <span className="absolute -bottom-1.5 left-0 h-px w-0 bg-signal transition-all duration-300 group-hover:w-full" />
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid="header-cta"
            onClick={() => go("contact")}
            className="hidden rounded-full bg-signal px-5 py-2.5 font-sans text-sm font-semibold text-bg-primary transition-transform duration-200 hover:scale-[1.03] active:scale-95 sm:block"
          >
            Get a workflow audit
          </button>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="grid h-10 w-10 place-items-center rounded-lg border border-hairline text-ink md:hidden"
            aria-label="Toggle menu"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="border-t border-hairline bg-bg-primary/95 backdrop-blur-xl md:hidden">
          <div className="band-pad flex flex-col gap-1 py-4">
            {NAV.map((n) => (
              <button
                key={n.id}
                data-testid={`mobile-nav-${n.id}`}
                onClick={() => go(n.id)}
                className="rounded-lg px-3 py-3 text-left font-sans text-base text-ink-muted hover:bg-white/5 hover:text-ink"
              >
                {n.label}
              </button>
            ))}
            <button
              onClick={() => go("contact")}
              className="mt-2 rounded-full bg-signal px-5 py-3 text-center font-sans text-sm font-semibold text-bg-primary"
            >
              Get a workflow audit
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
